import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import type { IEventSubTypeConfiguration, IWorkspaceTile } from '../hooks/useEventSubTypeConfig';

const DEFAULT_TILE_ITEMS: readonly IWorkspaceTile[] = [
  {
    componentId: 'event_countdown',
    route: '/edit/settings',
  },
  {
    componentId: 'concierge',
    link: 'https://withjoy.typeform.com/to/fT5TU4VL',
  },
  { componentId: 'website' },
];

export const useCarbonDefaultConfig = (): IEventSubTypeConfiguration => {
  const { tq } = useTranslation('eventWorkspace');

  return useMemo<IEventSubTypeConfiguration>(
    () => ({
      category: 'wedding',
      categorySubType: 'default',
      title: 'Wedding',
      description: 'Event template for wedding events with useCarbon flag.',
      insights: ['Additional AI friendly descriptors here to help AI have deeper context into what this event type and data structure is about'],
      location: {
        city: 'San Francisco',
        state: 'CA',
        country: 'US',
      },
      date: 'ISO 8601 date with timezone offset here',
      eventImage: 'https://withjoy.com/assets/public/proto/modern/corpor',
      eventIcon: '',
      apps: [
        {
          appId: 'website',
          label: tq`Website`,
          route: '/edit/design',
          color: {
            start: 'rgba(82, 146, 241, 1)',
            end: 'rgba(38, 100, 194, 1)',
          },
          description: tq`Design and customize your event website`,
          keywords: ['website', 'design', 'customize', 'event'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/website.png',
        },
        {
          label: tq`Guest Stays`,
          route: '/edit/accommodations',
          color: {
            start: 'rgba(217, 140, 255, 1)',
            end: 'rgba(82, 41, 164, 1)',
          },
          description: tq`Simplify guest accommodations while saving them money`,
          keywords: ['guest', 'stays', 'accommodations', 'hotels', 'travel'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/stays.png',
        },
        {
          appId: 'event_schedule',
          label: tq`Schedule`,
          link: '${eventHandle}/edit/schedule',
          color: {
            start: 'rgba(255, 225, 155, 1)',
            end: 'rgba(255, 208, 98, 1)',
          },
          description: tq`Event schedule to define times and locations for various activities during the event`,
          keywords: ['event', 'schedule', 'agenda', 'itinerary', 'times', 'dates', 'locations'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/schedule.png',
        },
        {
          appId: 'guest_list',
          label: tq`Guests`,
          link: '${eventHandle}/edit/guests',
          color: {
            start: 'rgba(255, 225, 155, 1)',
            end: 'rgba(255, 208, 98, 1)',
          },
          background: 'linear-gradient(140.45deg, #806BE0 0%, #492DD9 111.55%)',
          description: tq`Manage the guests attending your event, including RSVPs, dietary restrictions, and more`,
          keywords: ['guest', 'list', 'manage', 'rsvp', 'dietary', 'restrictions'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/guests_v1.svg',
        },
        {
          appId: 'vip',
          label: tq`Speakers`,
          link: '${eventHandle}/edit/vip',
          color: {
            start: 'rgba(255, 225, 155, 1)',
            end: 'rgba(255, 208, 98, 1)',
          },
          background: 'linear-gradient(140.45deg, #806BE0 0%, #492DD9 111.55%)',
          description: tq`Manage the your event speakers`,
          keywords: ['vip', 'speaker'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/app-vip.png',
        },
        /*
        {
          label: tq`Save the Date`,
          route: '/edit/cards/wedding/save-the-date',
          color: {
            start: 'rgba(140, 255, 217, 1)',
            end: 'rgba(25, 183, 131, 1)',
          },
          description: tq`Create and send a Save the Date for your wedding`,
          keywords: [],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/save_the_date.png',
        },
        */
        /*
        {
          label: tq`Invitation`,
          route: '/edit/cards/wedding/invitation',
          color: {
            start: 'rgba(140, 255, 217, 1)',
            end: 'rgba(25, 183, 131, 1)',
          },
          description: tq`Design and send invitations for your wedding`,
          keywords: ['wedding', 'invitation', 'design', 'send'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/invitations.png',
        },
        */
        /*
        {
          label: tq`Day of`,
          route: '/edit/cards/wedding/menu',
          color: {
            start: 'rgba(140, 255, 217, 1)',
            end: 'rgba(25, 183, 131, 1)',
          },
          description: tq`Browse signs, menus, and other stationery for your wedding`,
          keywords: ['stationery', 'signs', 'menus', 'wedding'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/stationery.png',
        },
        */
        {
          label: tq`Registration`,
          link: '${eventHandle}/edit/rsvp',
          color: {
            start: 'rgba(255, 211, 36, 1)',
            end: 'rgba(255, 211, 36, 1)',
          },
          description: tq`Manage your event RSVPs, including guest responses and dietary restrictions`,
          keywords: ['rsvp', 'manage', 'guest', 'responses', 'dietary', 'restrictions'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/rsvp.png',
        },
        {
          label: tq`Messaging`,
          route: '/edit/messaging',
          color: {
            start: 'rgba(138, 255, 152, 1)',
            end: 'rgba(0, 205, 24, 1)',
          },
          description: tq`Communicate with your guests through messaging`,
          keywords: ['email', 'sms', 'messaging', 'communication', 'guest', 'notify', 'remind'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/messaging.png',
        },
        {
          appId: 'settings',
          label: tq`Settings`,
          route: '/edit/settings',
          color: {
            start: 'rgba(215, 220, 226, 1)',
            end: 'rgba(110, 113, 119, 1)',
          },
          background: 'linear-gradient(140.91deg, #6C6B6B 1.75%, #202020 93.35%)',
          description: tq`Event settings to manage your event details, including title, date, location, and more`,
          keywords: ['settings', 'date', 'location', 'configure', 'manage'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/settings_v1.svg',
        },
        {
          appId: 'concierge',
          label: tq`Concierge`,
          link: 'https://withjoy.typeform.com/to/fT5TU4VL',
          color: {
            start: 'rgba(82, 146, 241, 1)',
            end: 'rgba(38, 100, 194, 1)',
          },
          background: `linear-gradient(149.23deg, #25AFFA 11.66%, rgba(73, 81, 239, 0.9) 52.28%, rgba(206, 84, 179, 0.8) 86.43%)`,
          description: tq`Event settings to manage your event details, including title, date, location, and more`,
          keywords: ['settings', 'date', 'location', 'configure', 'manage'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/concierge_v1.svg',
        },
      ],
      tiles: DEFAULT_TILE_ITEMS,
    }),
    [tq],
  );
};
